import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { api } from '../lib/api';

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const [tree, setTree] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [trending, setTrending] = useState([]);
  const [stats, setStats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [branding, setBranding] = useState({});
  const [footerCfg, setFooterCfg] = useState({});
  const [seoHome, setSeoHome] = useState({});
  const [cities, setCities] = useState([]);
  const [nav, setNav] = useState({ show_mega_menu: true });
  const [pwa, setPwa] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [t, s, tr, st, br, bd, fc, sh, ct, nv, pw] = await Promise.all([
          api.getCategoriesTree(),
          api.getSuppliers(),
          api.getTrendingSearches(),
          api.getStats(),
          api.getBrands(),
          api.getBranding().catch(() => ({})),
          api.getFooterSettings().catch(() => ({})),
          api.getSeoHome().catch(() => ({})),
          api.getCities().catch(() => []),
          api.getNavSettings().catch(() => ({ show_mega_menu: true })),
          api.getPwaManifest().catch(() => ({})),
        ]);
        if (!mounted) return;
        setTree(t || []);
        setSuppliers(s || []);
        setTrending(tr || []);
        setStats(st || []);
        setBrands(br || []);
        setBranding(bd || {});
        setFooterCfg(fc || {});
        setSeoHome(sh || {});
        setCities(ct || []);
        setNav(nv || { show_mega_menu: true });
        // Re-shape the manifest payload into a flatter object the rest of
        // the app can consume without knowing PWA shape:
        //   { apple_touch_icon_url, icon_192_url, theme_color, ... }
        setPwa({
          icon_192_url: pw?.icons?.find?.((i) => i.sizes === '192x192')?.src || '',
          icon_512_url: pw?.icons?.find?.((i) => i.sizes === '512x512')?.src || '',
          apple_touch_icon_url: pw?.icons?.find?.((i) => i.sizes === '192x192')?.src || '',
          theme_color: pw?.theme_color,
          background_color: pw?.background_color,
        });
      } catch (e) {
        console.error('Data load failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Derived lookup maps (memoised so consumers don't re-render when sibling state changes)
  const derived = useMemo(() => {
    const supplierById = Object.fromEntries(suppliers.map((s) => [s.id, s]));
    const categories = tree.flatMap((m) => (m.categories || []).map((c) => ({ ...c, main: m })));
    const categoryById = Object.fromEntries(categories.map((c) => [c.id, c]));
    const categoryBySlug = Object.fromEntries(categories.map((c) => [c.slug, c]));
    const subCategories = categories.flatMap((c) => (c.sub_categories || []).map((sc) => ({ ...sc, category: c, main: c.main })));
    const subById = Object.fromEntries(subCategories.map((s) => [s.id, s]));
    const subBySlug = Object.fromEntries(subCategories.map((s) => [s.slug, s]));
    return { supplierById, categories, categoryById, categoryBySlug, subCategories, subById, subBySlug };
  }, [suppliers, tree]);

  const value = useMemo(() => ({
    tree, suppliers, trending, stats, brands, loading,
    ...derived,
    branding, seoHome, cities, footerCfg, nav, pwa,
  }), [tree, suppliers, trending, stats, brands, loading, derived, branding, seoHome, cities, footerCfg, nav, pwa]);

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
